import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanDisplay extends FloorplanProductItem<DisplayConfiguration> {
  @Exclude()
  private readonly _display: DisplayConfiguration;

  @IsString()
  @Expose({ name: "displayId" })
  private readonly _displayId: string;

  constructor(
    floorplan: Floorplan,
    display: DisplayConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, display, id, x, y, size, notes);
    this._displayId = display?.id;
    this._display = display;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.displaysItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanDisplay) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanDisplay.create(this._floorplan, config, x, y, size);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.displays;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanDisplay,
        this._floorplan.displays,
        FloorplanEventType.DISPLAY_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanDisplay) => void) | undefined,
      );
  }

  override delete() {
    this._floorplan.deleteDisplay(this);
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshDisplaysLock();
  }

  protected override getConfigFromProject(floorplan: Floorplan): DisplayConfiguration | undefined {
    return floorplan.getDisplayConfig(this._displayId);
  }

  static create(floorplan: Floorplan, display: DisplayConfiguration, x: number, y: number, size: number): FloorplanDisplay {
    return new FloorplanDisplay(floorplan, display, uuidv4(), x, y, size);
  }
}
